/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { StyledText } from "../assets/typography/typography";
import { colors } from "./colors";
import { css } from "@emotion/react";

interface ButtonProps extends React.HTMLProps<HTMLButtonElement> {
  hoverColor: string;
  color: string;
  backgroundColor: string;
  icon?: React.ReactNode;
  buttonSize?: "large" | "small";
  type?: "submit" | "button" | "reset" | undefined;
}

const primaryButtonStyle = css`
  display: flex;
  flex-direction: row;
  border-style: none;
  justify-content: center;
  text-align: center;
  display: inline-flex;
  align-items: center;
  border-radius: 6px;
  padding: 12px 16px;
  gap: 8px;
`;

export const PrimaryButton = ({
  hoverColor,
  color,
  icon,
  width,
  backgroundColor,
  children,
  disabled,
  buttonSize = "large",
  ...rest
}: ButtonProps) => {
  const [bgColor, setBackgroundColor] = useState(backgroundColor);

  useEffect(() => {
    setBackgroundColor(backgroundColor);
  }, [backgroundColor]);

  const buttonStyle = css`
    ${primaryButtonStyle}
    ${!disabled &&
    `
    cursor: pointer;
    `}
    background-color: ${disabled ? colors.ctaInactive : bgColor};
    color: ${disabled ? colors.textSecondary : color};
    width: ${width};
    min-width: ${width || "max-content"};
  `;
  const textClassname =
    buttonSize === "large" ? "bodyTextSemibold" : "bodySubtextSemibold";

  return (
    <button
      css={buttonStyle}
      disabled={disabled}
      onMouseEnter={() => setBackgroundColor(hoverColor)}
      onMouseLeave={() => setBackgroundColor(backgroundColor)}
      {...rest}
    >
      {icon}
      <StyledText className={`${textClassname} mb-0`}>{children}</StyledText>
    </button>
  );
};

export const SecondaryButton = ({
  hoverColor,
  color,
  icon,
  width,
  className,
  backgroundColor,
  children,
  buttonSize = "large",
  style,
  ...rest
}: ButtonProps) => {
  const [bgColor, setBackgroundColor] = useState(backgroundColor);

  useEffect(() => {
    setBackgroundColor(backgroundColor);
  }, [backgroundColor]);

  const textClassname =
    buttonSize === "large" ? "bodyTextSemibold" : "bodySubtextSemibold";

  return (
    <button
      style={{
        backgroundColor: bgColor,
        color: color,
        borderColor: colors.greyBorder,
        width: width,
        minWidth: width || "max-content",
        textAlign: "center",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",
        ...style,
      }}
      className={`secondaryButton d-flex flex-row ${className}`}
      onMouseEnter={() => setBackgroundColor(hoverColor)}
      onMouseLeave={() => setBackgroundColor(backgroundColor)}
      {...rest}
    >
      {icon}
      <StyledText className={`${textClassname} mb-0`}>{children}</StyledText>
    </button>
  );
};

interface LinkButtonProps extends React.HTMLAttributes<HTMLAnchorElement> {
  hoverColor: string;
  color: string;
  width?: string;
  className?: string;
  children: React.ReactNode;
  backgroundColor: string;
  icon?: React.ReactNode;
  to: string;
}

export const LinkButton = ({
  hoverColor,
  color,
  icon,
  width,
  className,
  backgroundColor,
  children,
  to,
  style,
  ...rest
}: LinkButtonProps) => {
  const [bgColor, setBackgroundColor] = useState(backgroundColor);

  useEffect(() => {
    setBackgroundColor(backgroundColor);
  }, [backgroundColor]);

  return (
    <Link
      style={{
        backgroundColor: bgColor,
        color: color,
        width: width,
        minWidth: width || "max-content",
        gap: "8px",
        textDecoration: "none",
        ...style,
      }}
      className={`primaryButton d-flex flex-row ${className}`}
      to={to}
      onMouseEnter={() => setBackgroundColor(hoverColor)}
      onMouseLeave={() => setBackgroundColor(backgroundColor)}
      {...rest}
    >
      {icon}
      <StyledText className="bodyTextSemibold mb-0">{children}</StyledText>
    </Link>
  );
};

export const SmallButton = ({
  hoverColor,
  color,
  icon,
  width,
  className,
  backgroundColor,
  children,
  ...rest
}: ButtonProps) => {
  const [bgColor, setBackgroundColor] = useState(backgroundColor);

  useEffect(() => {
    setBackgroundColor(backgroundColor);
  }, [backgroundColor]);

  return (
    <button
      style={{
        backgroundColor: bgColor,
        color: color,
        width: width,
        minWidth: width || "max-content",
        gap: "8px",
      }}
      className={`smallButton d-flex flex-row ${className}`}
      onMouseEnter={() => setBackgroundColor(hoverColor)}
      onMouseLeave={() => setBackgroundColor(backgroundColor)}
      {...rest}
    >
      {icon}
      <StyledText className="bodySubtextSemibold mb-0">{children}</StyledText>
    </button>
  );
};
